// Theme definitions for each subscription tier.
// Each theme object contains design tokens applied as CSS variables + direct style values.
// Customer pages and the landing page are exempt — themes apply to tradie views only.

export const THEMES = {
  // FREE / default — classic orange brand
  tradie: {
    bgBody:
      "radial-gradient(circle at top left, rgba(255, 138, 0,0.09), transparent 28%), radial-gradient(circle at top right, rgba(255, 167, 51,0.06), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f7f7f6 45%, #ffffff 100%)",
    bgBodyDark:
      "radial-gradient(circle at top left, rgba(255, 138, 0,0.07), transparent 30%), linear-gradient(180deg, #121212 0%, #0D0D0D 100%)",
    headerBg: "var(--dark)",
    headerBorder: "1px solid rgba(255,255,255,0.07)",
    headerAccent: "var(--primary)",
    accent: "var(--primary)",
    accentDark: "#CC6E00",
    accentMid: "#FFA733",
    accentLight: "rgba(255, 138, 0,0.08)",
    accentGlow: "rgba(255, 138, 0,0.22)",
    accentSoft: "rgba(255, 138, 0,0.08)",
    primaryGradient:
      "linear-gradient(135deg, #CC6E00 0%, #FF8A00 55%, #FFA733 100%)",
    primaryShadow: "rgba(255, 138, 0,0.28)",
    cardHighlightBorder: "var(--primary)",
    cardHighlightBg: "rgba(255, 138, 0,0.025)",
    cardHighlightBadge: "var(--primary)",
    cardHighlightShadow: "rgba(255, 138, 0,0.10)",
  },

  // TRIAL — amber warm-up
  trial: {
    bgBody:
      "radial-gradient(circle at top left, rgba(245,158,11,0.07), transparent 28%), linear-gradient(180deg, #fffdf4 0%, #fef9e9 45%, #fdfaf0 100%)",
    bgBodyDark:
      "radial-gradient(circle at top left, rgba(245,158,11,0.05), transparent 30%), linear-gradient(180deg, #121212 0%, #0D0D0D 100%)",
    headerBg: "var(--dark)",
    headerBorder: "1px solid rgba(255,255,255,0.07)",
    headerAccent: "#F59E0B",
    accent: "#F59E0B",
    accentDark: "#D97706",
    accentMid: "#FBBF24",
    accentLight: "rgba(245,158,11,0.08)",
    accentGlow: "rgba(245,158,11,0.22)",
    accentSoft: "rgba(245,158,11,0.08)",
    primaryGradient:
      "linear-gradient(135deg, #D97706 0%, #F59E0B 55%, #FBBF24 100%)",
    primaryShadow: "rgba(245,158,11,0.28)",
    // Animated stripe for the trial header — uses amber tones instead of orange
    trialStripeGradient: "linear-gradient(90deg, #D97706, #F59E0B, #FBBF24, #F59E0B, #D97706)",
    cardHighlightBorder: "#F59E0B",
    cardHighlightBg: "rgba(245,158,11,0.025)",
    cardHighlightBadge: "#F59E0B",
    cardHighlightShadow: "rgba(245,158,11,0.10)",
  },

  // PRO (sole_trader) — steel blue
  pro: {
    bgBody:
      "radial-gradient(circle at top left, rgba(59,130,246,0.07), transparent 28%), linear-gradient(180deg, #f0f6ff 0%, #e8f0fd 45%, #f0f4ff 100%)",
    bgBodyDark:
      "radial-gradient(circle at top left, rgba(59,130,246,0.07), transparent 30%), linear-gradient(180deg, #0A0F1A 0%, #080C14 100%)",
    headerBg: "linear-gradient(135deg, #0F1F3D 0%, #162943 100%)",
    headerBorder: "1px solid rgba(255,255,255,0.1)",
    headerAccent: "#93C5FD",
    accent: "#3B82F6",
    accentDark: "#1D4ED8",
    accentMid: "#60A5FA",
    accentLight: "rgba(59,130,246,0.08)",
    accentGlow: "rgba(59,130,246,0.22)",
    accentSoft: "rgba(59,130,246,0.08)",
    primaryGradient:
      "linear-gradient(135deg, #1D4ED8 0%, #3B82F6 55%, #60A5FA 100%)",
    primaryShadow: "rgba(59,130,246,0.28)",
    cardHighlightBorder: "#3B82F6",
    cardHighlightBg: "rgba(59,130,246,0.025)",
    cardHighlightBadge: "#3B82F6",
    cardHighlightShadow: "rgba(59,130,246,0.10)",
  },

  // ULTIMATE (professional) — silver/platinum
  // headerBoxShadow: extra inset glow unique to this tier — used by Header.js
  ultimate: {
    bgBody:
      "radial-gradient(circle at top left, rgba(148,163,184,0.07), transparent 28%), linear-gradient(180deg, #f8fafc 0%, #f1f5f9 45%, #f8fafc 100%)",
    bgBodyDark:
      "radial-gradient(circle at top left, rgba(148,163,184,0.09), transparent 30%), linear-gradient(180deg, #0E1117 0%, #090B10 100%)",
    headerBg:
      "linear-gradient(135deg, #1B1F2B 0%, #2A2F3D 50%, #1B1F2B 100%)",
    headerBorder: "1px solid rgba(148,163,184,0.2)",
    headerBoxShadow: "0 1px 0 rgba(148,163,184,0.15) inset",
    headerAccent: "#CBD5E1",
    accent: "#94A3B8",
    accentDark: "#64748B",
    accentMid: "#CBD5E1",
    accentLight: "rgba(148,163,184,0.08)",
    accentGlow: "rgba(148,163,184,0.22)",
    accentSoft: "rgba(148,163,184,0.08)",
    primaryGradient:
      "linear-gradient(135deg, #64748B 0%, #94A3B8 55%, #CBD5E1 100%)",
    primaryShadow: "rgba(148,163,184,0.28)",
    cardHighlightBorder: "#94A3B8",
    cardHighlightBg: "rgba(148,163,184,0.025)",
    cardHighlightBadge: "#94A3B8",
    cardHighlightShadow: "rgba(148,163,184,0.10)",
  },

  // ENTERPRISE — near-black with gold accents
  enterprise: {
    bgBody:
      "radial-gradient(circle at top left, rgba(201,168,76,0.07), transparent 28%), linear-gradient(180deg, #fdfcf4 0%, #faf8e9 45%, #fdfcf4 100%)",
    bgBodyDark:
      "radial-gradient(circle at top left, rgba(201,168,76,0.07), transparent 30%), linear-gradient(180deg, #0A0A08 0%, #070705 100%)",
    headerBg: "#0A0A0A",
    headerBorder: "1px solid rgba(201,168,76,0.25)",
    headerAccent: "#C9A84C",
    accent: "#C9A84C",
    accentDark: "#A8883A",
    accentMid: "#DEC068",
    accentLight: "rgba(201,168,76,0.08)",
    accentGlow: "rgba(201,168,76,0.22)",
    accentSoft: "rgba(201,168,76,0.08)",
    primaryGradient:
      "linear-gradient(135deg, #A8883A 0%, #C9A84C 55%, #DEC068 100%)",
    primaryShadow: "rgba(201,168,76,0.28)",
    cardHighlightBorder: "#C9A84C",
    cardHighlightBg: "rgba(201,168,76,0.025)",
    cardHighlightBadge: "#C9A84C",
    cardHighlightShadow: "rgba(201,168,76,0.10)",
  },
};

// Map subscription state → tier key string
// Single source of truth for tier resolution — used by getTheme and Header.js
// to avoid duplicating the simulation/god-mode/real-tier priority cascade.
function normalizeTierKey(key, fallback = "free") {
  if (key === "tradie") return "free";
  return ["free", "trial", "pro", "ultimate", "enterprise"].includes(key)
    ? key
    : fallback;
}

export function getTierKey(subscription, fallbackTierKey = "free") {
  if (!subscription) return "free";
  if (subscription.loading) return normalizeTierKey(fallbackTierKey);
  if (subscription.simulatedTierId) {
    const simMap = { free: "free", trial: "trial", sole_trader: "pro", professional: "ultimate", enterprise: "enterprise" };
    return simMap[subscription.simulatedTierId] || "free";
  }
  if (subscription.isGodMode) return "enterprise";
  if (subscription.isTrialing) return "trial";
  if (subscription.isSoleTrader) return "pro";
  if (subscription.isProfessional) return "ultimate";
  if (subscription.isEnterprise) return "enterprise";
  return "free";
}

// Map subscription state → theme object (with stable id field for change detection)
// Mirrors the tier-variant logic in Header.js — keep in sync.
export function getTheme(subscription, fallbackTierKey = "free") {
  const key = getTierKey(subscription, fallbackTierKey);
  const themeKey = key === "free" ? "tradie" : key;
  const base = THEMES[themeKey] || THEMES.tradie;
  // Attach id so useTheme can skip redundant CSS writes via lastThemeId ref.
  // Mutate only when needed to keep the reference stable for useMemo.
  if (!base.id) base.id = themeKey;
  return base;
}
